import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { DocumentQueryResult, LoanDocumentQueryParams } from './document.model';
import { ConfigService } from '../config/config.service';
import { from, Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ResourceService } from '../resource/resource.service';
import { DocumentBaseService } from './document-base.service';
import {
  LoanFileTemplateData,
  OnDemandStatementEnvelope,
  OnDemandStatementRequest,
  OnDemandStatementStatus,
  StatementDownloadResult,
} from './loan-document.model';
import { GetFileData, GetFileInputData } from '../resource/resource.model';
import { SignedLoanDocumentFile } from './signed-loan-document-file.model';

@Injectable({
  providedIn: 'root'
})
export class LoanDocumentService extends DocumentBaseService {

  protected override _queryParams: LoanDocumentQueryParams = {};

  constructor(http: HttpClient, resourceService: ResourceService, configService: ConfigService) {
    super(http, resourceService, configService, `${configService.environment.loans.url}/api/files`)
  }

  override query(loanId: number, params: LoanDocumentQueryParams): Observable<DocumentQueryResult> {
    params.loanId = loanId;
    if (!params.sort)
      params.sort = '-createdon';
    return this.http.post<DocumentQueryResult>(this.baseUrl, params);
  }

  override queryByUniqueId(loanUniqueId: string, params: LoanDocumentQueryParams): Observable<DocumentQueryResult> {
    params.loanUniqueId = loanUniqueId;
    if (!params.sort)
      params.sort = '-createdon';
    return this.http.post<DocumentQueryResult>(`${this.baseUrl}/uniqueId`, params);
  }

  override delete(fileId: number, _: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/${fileId}`);
  }

  override download(fileId: number, _: number): void {
    this.resourceService.download({ url: `${this.baseUrl}/${fileId}` }).subscribe();
  }

  getFileData(fileId: number, trustUrl: boolean = false): Observable<GetFileData> {
    const inputData: GetFileInputData = {
      url: `${this.baseUrl}/${fileId}`,
      trustUrl,
    }
    return this.resourceService.getFileData(inputData);
  }

  generateFileFromTemplate(loanId: number, templateId: number, trustUrl: boolean = false): Observable<GetFileData> {
    const inputData: GetFileInputData = {
      url: `${this.baseUrl}/${loanId}/generate/${templateId}`,
      trustUrl,
    }
    return this.resourceService.postForFileData(inputData, {});
  }

  // Success returns { kind:'file' } WITHOUT downloading; any non-success is parsed to { kind:'status' }.
  downloadOnDemandStatement(
    loanId: number,
    templateId: number,
    request: OnDemandStatementRequest
  ): Observable<StatementDownloadResult> {
    const body: OnDemandStatementRequest = {
      granularity: request.granularity,
      year: request.year,
    };
    if (request.granularity === 'month' && request.month != null)
      body.month = request.month;
    if (request.format)
      body.format = request.format;

    return this.http
      .post(`${this.baseUrl}/${loanId}/generate/${templateId}`, body, {
        responseType: 'blob',
        observe: 'response',
      })
      .pipe(
        map((response: HttpResponse<Blob>): StatementDownloadResult => ({
          kind: 'file',
          blob: response.body,
          filename: this.resolveFilename(response),
        })),
        catchError((error: unknown) => this.parseStatementErrorEnvelope(error))
      );
  }

  clearCache(loanId: number): Observable<number> {
    return this.http.delete<number>(`${this.baseUrl}/${loanId}/cache`);
  }

  getLoanFileTemplates(loanId: number): Observable<LoanFileTemplateData[]> {
    return this.http.get<LoanFileTemplateData[]>(`${this.baseUrl}/${loanId}/templates`);
  }

  getSignatures(fileId: number): Observable<SignedLoanDocumentFile> {
    return this.http.get<SignedLoanDocumentFile>(`${this.baseUrl}/${fileId}/signatures`);
  }

  // observe:'response' is required to read the Content-Disposition header.
  private resolveFilename(response: HttpResponse<Blob>): string {
    let filename = 'download.bin';
    const disposition = response.headers.get('content-disposition');
    if (disposition && disposition.indexOf('attachment') !== -1) {
      const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
      const matches = filenameRegex.exec(disposition);
      if (matches != null && matches[1])
        filename = matches[1].replace(/['"]/g, '');
    }
    return filename;
  }

  // Anchor-click download that revokes the object URL (the shared download() leaks it).
  downloadStatementFile(blob: Blob, filename: string): void {
    if (navigator.msSaveBlob) {
      navigator.msSaveBlob(blob, filename);
      return;
    }
    const objectUrl = window.URL.createObjectURL(blob);
    try {
      const linkElement = document.createElement('a');
      linkElement.setAttribute('href', objectUrl);
      linkElement.setAttribute('download', filename);
      const clickEvent = new MouseEvent('click', { view: window, bubbles: true, cancelable: false });
      linkElement.dispatchEvent(clickEvent);
    } finally {
      window.URL.revokeObjectURL(objectUrl);
    }
  }

  // Non-2xx blob body: parse the Blob-wrapped JSON envelope; a non-JSON body falls back to a generic error.
  private parseStatementErrorEnvelope(error: unknown): Observable<StatementDownloadResult> {
    const genericEnvelope: OnDemandStatementEnvelope = {
      status: OnDemandStatementStatus.Error,
      message: "We couldn't generate your statement. Please try again later.",
    };

    if (!(error instanceof HttpErrorResponse) || !(error.error instanceof Blob))
      return of<StatementDownloadResult>({ kind: 'status', envelope: genericEnvelope });

    return from(error.error.text()).pipe(
      map((text: string): StatementDownloadResult => {
        try {
          const parsed = JSON.parse(text) as OnDemandStatementEnvelope;
          // Explicit null check: NoActivity is 0, which is falsy.
          if (parsed && parsed.status != null)
            return { kind: 'status', envelope: parsed };
        } catch {
          // non-JSON body — generic fallback below
        }
        return { kind: 'status', envelope: genericEnvelope };
      }),
      catchError(() => of<StatementDownloadResult>({ kind: 'status', envelope: genericEnvelope }))
    );
  }
}
